/**
 * "Questions you might be asking yourself…" — the homepage carousel.
 *
 * Each card is real HTML text (never text baked into the illustration) and
 * links to a public, indexable article that answers that exact question.
 * Where a strong article already exists it is reused rather than duplicated,
 * so two related questions may point at the same page on purpose.
 */

import whatIsTrauma from "@/assets/questions/what-is-trauma.jpg";
import traumaResponse from "@/assets/questions/trauma-response.jpg";
import signsEmotional from "@/assets/questions/signs-emotional-trauma.jpg";
import whatIsEmotional from "@/assets/questions/what-is-emotional-trauma.jpg";
import overcomeTrauma from "@/assets/questions/overcome-trauma.jpg";
import childhoodTrauma from "@/assets/questions/childhood-trauma.jpg";
import childhoodAdult from "@/assets/questions/childhood-trauma-adult.jpg";
import abandonment from "@/assets/questions/abandonment-trauma.jpg";
import betrayal from "@/assets/questions/betrayal-trauma.jpg";
import whySoHard from "@/assets/questions/why-so-hard.jpg";
import canBeOvercome from "@/assets/questions/can-trauma-be-overcome.jpg";
import howLong from "@/assets/questions/how-long.jpg";
import helpSomeone from "@/assets/questions/help-someone.jpg";

export type HomepageQuestion = {
  id: string;
  /** The question exactly as a visitor would ask it. Rendered as text. */
  question: string;
  /** Three short words under the illustration. */
  note: string;
  /** Published, public blog article answering the question. */
  slug: string;
  image: string;
  /** Soft card wash — gold/champagne/cream family only. */
  tint: string;
};

export const HOMEPAGE_QUESTIONS: HomepageQuestion[] = [
  {
    id: "what-is-trauma",
    question: "What is trauma?",
    note: "Understand. Heal. Grow.",
    slug: "what-is-trauma",
    image: whatIsTrauma,
    tint: "bg-[color-mix(in_oklab,var(--rose-soft,#f6e3e3)_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "trauma-response",
    question: "What is a trauma response?",
    note: "Recognise. Understand. Feel safer.",
    slug: "trauma-responses",
    image: traumaResponse,
    tint: "bg-[color-mix(in_oklab,#dde5da_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "signs-emotional-trauma",
    question: "Signs of emotional trauma in adults?",
    note: "Notice. Validate. Take the next step.",
    slug: "signs-of-emotional-trauma-in-adults",
    image: signsEmotional,
    tint: "bg-[color-mix(in_oklab,#f7ebd2_60%,var(--cream,#fdf8f0))]",
  },
  {
    id: "what-is-emotional-trauma",
    question: "What is emotional trauma?",
    note: "When the wound is not visible.",
    slug: "what-is-emotional-trauma",
    image: whatIsEmotional,
    tint: "bg-[color-mix(in_oklab,var(--rose-soft,#f6e3e3)_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "overcome-trauma",
    question: "How to overcome trauma?",
    note: "Learn. Practise. Move forward.",
    slug: "how-to-overcome-trauma",
    image: overcomeTrauma,
    tint: "bg-[color-mix(in_oklab,#f7ebd2_60%,var(--cream,#fdf8f0))]",
  },
  {
    id: "childhood-trauma",
    question: "How to overcome childhood trauma?",
    note: "Gently. At your own pace.",
    slug: "how-to-heal-from-childhood-trauma-as-an-adult",
    image: childhoodTrauma,
    tint: "bg-[color-mix(in_oklab,#dde5da_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "childhood-trauma-adult",
    question: "How to overcome childhood trauma as an adult?",
    note: "Meet the child you were.",
    slug: "how-to-heal-from-childhood-trauma-as-an-adult",
    image: childhoodAdult,
    tint: "bg-[color-mix(in_oklab,var(--rose-soft,#f6e3e3)_50%,var(--cream,#fdf8f0))]",
  },
  {
    id: "abandonment",
    question: "How to overcome abandonment trauma?",
    note: "Closeness can feel safe again.",
    slug: "fear-of-abandonment-after-trauma",
    image: abandonment,
    tint: "bg-[color-mix(in_oklab,#dde5da_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "betrayal",
    question: "How to overcome betrayal trauma?",
    note: "Rebuild trust in yourself first.",
    slug: "how-to-overcome-betrayal-trauma",
    image: betrayal,
    tint: "bg-[color-mix(in_oklab,var(--rose-soft,#f6e3e3)_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "why-so-hard",
    question: "Why is childhood trauma so hard to overcome?",
    note: "It shaped the roots.",
    slug: "why-is-childhood-trauma-so-hard-to-overcome",
    image: whySoHard,
    tint: "bg-[color-mix(in_oklab,#dde5da_55%,var(--cream,#fdf8f0))]",
  },
  {
    id: "can-trauma-be-overcome",
    question: "Can trauma be overcome?",
    note: "An honest answer.",
    slug: "can-trauma-be-overcome",
    image: canBeOvercome,
    tint: "bg-[color-mix(in_oklab,#f7ebd2_60%,var(--cream,#fdf8f0))]",
  },
  {
    id: "how-long",
    question: "How long does it take to overcome trauma?",
    note: "No deadlines here.",
    slug: "how-long-does-trauma-recovery-take",
    image: howLong,
    tint: "bg-[color-mix(in_oklab,#f7ebd2_60%,var(--cream,#fdf8f0))]",
  },
  {
    id: "help-someone",
    question: "How to help someone overcome trauma?",
    note: "Steady. Patient. Present.",
    slug: "how-to-help-someone-overcome-trauma",
    image: helpSomeone,
    tint: "bg-[color-mix(in_oklab,var(--rose-soft,#f6e3e3)_55%,var(--cream,#fdf8f0))]",
  },
];
